import { useState } from 'react';
import { AccountModel, HanThanhToan, KhachHang, QuyenChungTu, Tax, TinhChatThue } from '@/types/schemas';

export interface FormFieldState {
  // Supplier information
  nhaCungCap: KhachHang | null;
  taiKhoan: AccountModel | null;
  hanThanhToan: HanThanhToan | null;
  dienGiai: string;

  // Document information
  quyenChungTu: QuyenChungTu | null;
  soChungTu: string;

  // Tax information
  taxModel: Tax | null;
  taiKhoanThue: AccountModel | null;
  cucThue: KhachHang | null;
  tinhChatThue: TinhChatThue | null;
}

export interface FormFieldActions {
  // Search field setters
  setNhaCungCap: (nhaCungCap: KhachHang) => void;
  setTaiKhoan: (taiKhoan: AccountModel) => void;
  setHanThanhToan: (hanThanhToan: HanThanhToan) => void;
  setQuyenChungTu: (quyenChungTu: QuyenChungTu) => void;
  setSoChungTu: (soChungTu: string) => void;
  setDienGiai: (dienGiai: string) => void;

  setTaxModel: (taxModel: Tax) => void;
  setTaiKhoanThue: (taiKhoanThue: AccountModel) => void;
  setCucThue: (cucThue: KhachHang) => void;
  setTinhChatThue: (tinhChatThue: TinhChatThue) => void;

  // Utility functions
  resetState: () => void;
  updateState: (updates: Partial<FormFieldState>) => void;
}

export interface UseFormFieldStateReturn {
  state: FormFieldState;
  actions: FormFieldActions;
}

const initialState: FormFieldState = {
  // Supplier information
  nhaCungCap: null,
  taiKhoan: null,
  hanThanhToan: null,
  dienGiai: '',

  // Document information
  soChungTu: '',
  quyenChungTu: null,

  // Tax information
  taxModel: null,
  taiKhoanThue: null,
  cucThue: null,
  tinhChatThue: null
};

function transformInitialData(initialData?: MuaHangRecord): Partial<FormFieldState> {
  if (!initialData) return {};

  return {
    // Supplier information
    nhaCungCap: initialData.ma_kh_data || null,
    taiKhoan: initialData.tk_data || null,
    hanThanhToan: initialData.ma_tt_data || null,
    dienGiai: initialData.dien_giai || null,

    // Document information
    soChungTu: initialData.so_ct || '',
    quyenChungTu: initialData.ma_nk_data || { uuid: initialData.ma_nk },

    // Tax information
    taxModel: initialData.ma_thue_data || null,
    taiKhoanThue: initialData.tk_thue_data || null,
    cucThue: initialData.ma_kh9_data || null,
    tinhChatThue: initialData.ma_tc_thue_data || null
  };
}

export function useFormFieldState(initialData?: MuaHangRecord): UseFormFieldStateReturn {
  const [state, setState] = useState<FormFieldState>({
    ...initialState,
    ...transformInitialData(initialData)
  });

  const actions: FormFieldActions = {
    // Search field setters
    setNhaCungCap: (nhaCungCap: KhachHang) => {
      setState(prev => ({
        ...prev,
        nhaCungCap
      }));
    },
    setDienGiai: (dienGiai: string) => {
      setState(prev => ({
        ...prev,
        dienGiai
      }));
    },

    setTaiKhoan: (taiKhoan: AccountModel) => {
      setState(prev => ({
        ...prev,
        taiKhoan
      }));
    },

    setHanThanhToan: (hanThanhToan: HanThanhToan) => {
      setState(prev => ({
        ...prev,
        hanThanhToan
      }));
    },

    setQuyenChungTu: (quyenChungTu: QuyenChungTu) => {
      setState(prev => ({
        ...prev,
        quyenChungTu
      }));
    },

    setTaxModel: (taxModel: Tax) => {
      setState(prev => ({
        ...prev,
        taxModel
      }));
    },

    setTaiKhoanThue: (taiKhoanThue: AccountModel) => {
      setState(prev => ({
        ...prev,
        taiKhoanThue
      }));
    },

    setCucThue: (cucThue: KhachHang) => {
      setState(prev => ({
        ...prev,
        cucThue
      }));
    },

    setSoChungTu: (soChungTu: string) => {
      setState(prev => ({ ...prev, soChungTu }));
    },

    setTinhChatThue: (tinhChatThue: TinhChatThue) => {
      setState(prev => ({
        ...prev,
        tinhChatThue
      }));
    },

    resetState: () => {
      setState(initialState);
    },

    updateState: (updates: Partial<FormFieldState>) => {
      setState(prev => ({ ...prev, ...updates }));
    }
  };

  return { state, actions };
}
